import { createAsyncThunk } from '@reduxjs/toolkit'
import { handleError, hasError } from '@repo/shared/api/helpers'

import { AsyncThunkConfig, RejectValue } from '@/store/actions'
import { BeneficiaryTaskFields } from '@/store/spi/api'
import { Task } from '@/store/task'

export const task = {
  app: {
    delete: createAsyncThunk<
      { success: true; notify: boolean },
      { task: Task; notify?: boolean },
      AsyncThunkConfig
    >(
      'DELETE_TASKS',
      async ({ task, notify = true }, { extra, fulfillWithValue, rejectWithValue, getState }) => {
        const { altApi } = extra
        const beneficiaryId = getState().beneficiary.currentBeneficiaryId
        if (!beneficiaryId) return rejectWithValue(RejectValue.NoBeneficiary)
        if (!beneficiaryId) return rejectWithValue(RejectValue.NoBeneficiary)
        const organizationId = getState().user.currentOrganizationId
        if (!organizationId) return rejectWithValue(RejectValue.NoOrganization)

        const rep = await altApi.tasks.deleteOne({
          beneficiaryId,
          organizationId,
          taskId: task.id,
        })
        if (hasError(rep)) return rejectWithValue(handleError(rep))
        return fulfillWithValue({ ...rep, notify })
      },
    ),

    restore: createAsyncThunk<
      { task: Task; notify?: boolean },
      { task: Task; notify?: boolean },
      AsyncThunkConfig
    >(
      'RESTORE_TASK',
      async (
        { task: { id: taskId }, notify = true },
        { extra, fulfillWithValue, rejectWithValue, getState },
      ) => {
        const { altApi } = extra
        const beneficiaryId = getState().beneficiary.currentBeneficiaryId
        if (!beneficiaryId) return rejectWithValue(RejectValue.NoBeneficiary)
        const organizationId = getState().user.currentOrganizationId
        if (!organizationId) return rejectWithValue(RejectValue.NoOrganization)

        const task = await altApi.tasks.restoreOne({
          beneficiaryId,
          organizationId,
          taskId,
        })
        if (hasError(task)) return rejectWithValue(handleError(task))
        return fulfillWithValue({ task, notify })
      },
    ),

    create: createAsyncThunk<
      { task: Task },
      {
        fields: Pick<
          Task,
          'title' | 'content' | 'dueDate' | 'categories' | 'cta' | 'durationInHours'
        >
      },
      AsyncThunkConfig
    >('CREATE_TASK', async ({ fields }, { extra, fulfillWithValue, rejectWithValue, getState }) => {
      const { altApi } = extra
      const beneficiaryId = getState().beneficiary.currentBeneficiaryId
      if (!beneficiaryId) return rejectWithValue(RejectValue.NoBeneficiary)
      const organizationId = getState().user.currentOrganizationId
      if (!organizationId) return rejectWithValue(RejectValue.NoOrganization)

      const task = await altApi.tasks.createOne({
        beneficiaryId,
        organizationId,
        fields,
      })
      if (hasError(task)) return rejectWithValue(handleError(task))
      return fulfillWithValue({ task })
    }),

    archive: createAsyncThunk<
      { task: Task; notify: boolean },
      { task: Task; notify?: boolean },
      AsyncThunkConfig
    >(
      'ARCHIVE_TASK',
      async (
        { task: { id: taskId }, notify = true },
        { extra, fulfillWithValue, rejectWithValue, getState },
      ) => {
        const { altApi } = extra
        const beneficiaryId = getState().beneficiary.currentBeneficiaryId
        if (!beneficiaryId) return rejectWithValue(RejectValue.NoBeneficiary)
        const organizationId = getState().user.currentOrganizationId
        if (!organizationId) return rejectWithValue(RejectValue.NoOrganization)

        const task = await altApi.tasks.updateOne({
          beneficiaryId,
          fields: { archivedAt: new Date().getTime() },
          organizationId,
          taskId,
        })
        if (hasError(task)) return rejectWithValue(handleError(task))
        return fulfillWithValue({ task, notify })
      },
    ),

    unarchive: createAsyncThunk<
      { task: Task; notify: boolean },
      { task: Task; notify?: boolean },
      AsyncThunkConfig
    >(
      'UNARCHIVE_TASK',
      async (
        { task: { id: taskId }, notify = true },
        { extra, fulfillWithValue, rejectWithValue, getState },
      ) => {
        const { altApi } = extra
        const beneficiaryId = getState().beneficiary.currentBeneficiaryId
        if (!beneficiaryId) return rejectWithValue(RejectValue.NoBeneficiary)
        const organizationId = getState().user.currentOrganizationId
        if (!organizationId) return rejectWithValue(RejectValue.NoOrganization)

        const task = await altApi.tasks.updateOne({
          beneficiaryId,
          organizationId,
          taskId,
          // @ts-expect-error because backend needs null to remove the date
          fields: { archivedAt: null },
        })
        if (hasError(task)) return rejectWithValue(handleError(task))
        return fulfillWithValue({ task, notify })
      },
    ),

    update: createAsyncThunk<
      { task: Task },
      {
        taskId: string
        fields: BeneficiaryTaskFields
      },
      AsyncThunkConfig
    >(
      'UPDATE_TASK',
      async ({ taskId, fields }, { extra, fulfillWithValue, rejectWithValue, getState }) => {
        const { altApi } = extra
        const beneficiaryId = getState().beneficiary.currentBeneficiaryId
        if (!beneficiaryId) return rejectWithValue(RejectValue.NoBeneficiary)
        const organizationId = getState().user.currentOrganizationId
        if (!organizationId) return rejectWithValue(RejectValue.NoOrganization)

        const task = await altApi.tasks.updateOne({
          beneficiaryId,
          organizationId,
          taskId,
          fields,
        })
        if (hasError(task)) return rejectWithValue(handleError(task))
        return fulfillWithValue({ task })
      },
    ),

    updateDueDate: createAsyncThunk<
      { task: Task },
      { taskId: string; dueDate: Task['dueDate'] },
      AsyncThunkConfig
    >(
      'UPDATE_TASK_DUE_DATE',
      async ({ taskId, dueDate }, { extra, fulfillWithValue, rejectWithValue, getState }) => {
        const { altApi } = extra
        const beneficiaryId = getState().beneficiary.currentBeneficiaryId
        if (!beneficiaryId) return rejectWithValue(RejectValue.NoBeneficiary)
        const organizationId = getState().user.currentOrganizationId
        if (!organizationId) return rejectWithValue(RejectValue.NoOrganization)

        const task = await altApi.tasks.updateOne({
          beneficiaryId,
          organizationId,
          taskId,
          fields: {
            id: taskId,
            // @ts-expect-error because backend needs null to remove the date
            dueDate: dueDate ? dueDate : null,
          },
        })
        if (hasError(task)) return rejectWithValue(handleError(task))
        return fulfillWithValue({ task })
      },
    ),
  },
}
